import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-number-field>` says about itself.
 *
 * It is `CaosBaseField` with the inner input's type set to `number`, plus one thing of its own: it
 * puts a `numeric` marker attribute on itself when it connects, and the shared stylesheet keys
 * `font-variant-numeric: tabular-nums` off that marker. So a column of these line up on the decimal
 * point without the page doing anything. The marker is set in `connectedCallback` and NOT in the
 * constructor, because a custom-element constructor may not add attributes — doing it there makes
 * `document.createElement('caos-number-field')` throw, which is how FieldRenderer creates it.
 *
 * `numeric` is therefore not listed as an input: the piece sets it for itself and an author has no
 * reason to.
 *
 * PRECISION IS THE PAGE'S TO STATE. The field forwards `min`, `max` and `step` straight to the
 * inner input and does no rounding, no clamping and no validation of its own — a value outside the
 * bounds is typed, kept, and reported by the browser's own constraint validation. `invalid` and
 * `message` are set by whatever is checking the value, not by this piece.
 */
const docs: ComponentDocs = {
  aliases: ['numeric input', 'number input', 'decimal field', 'quantity field', 'spinner', 'slider', 'range', 'range slider'],

  answers: [
    'ask for a measurement or a quantity',
    'keep a column of numbers lined up on the decimal point',
    'hold a value to a fixed number of decimal places',
    'bound what can be entered to a range and a step',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        "The field's name, drawn above the control and pointed at the input, so clicking it focuses the " +
        'box. Left unset, no label is drawn.',
    },
    {
      name: 'placeholder',
      attribute: 'placeholder',
      type: 'text',
      description: 'The greyed hint inside an empty box.',
    },
    {
      name: 'value',
      attribute: 'value',
      type: 'number',
      description:
        'The current number. It is written back to this attribute on every keystroke, so the attribute ' +
        'and what is on screen stay the same thing.',
    },
    {
      name: 'min',
      attribute: 'min',
      type: 'number',
      description: 'The lowest value the browser will accept. Forwarded to the inner input; nothing here clamps.',
    },
    {
      name: 'max',
      attribute: 'max',
      type: 'number',
      description: 'The highest value the browser will accept. Forwarded to the inner input; nothing here clamps.',
    },
    {
      name: 'step',
      attribute: 'step',
      type: 'number',
      description:
        'The increment the stepper moves by, and the precision the browser enforces — `0.25` for a ' +
        'quarter-inch, `0.01` for cents.',
    },
    {
      name: 'name',
      attribute: 'name',
      type: 'text',
      description: 'The form name forwarded to the inner input, for a field submitted as part of a form.',
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'boolean',
      description: 'This field has to be filled in. Draws the asterisk beside the label and marks the input required.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description: 'You may not type in this. Greys the box and refuses focus.',
      default: false,
    },
    {
      name: 'invalid',
      attribute: 'invalid',
      type: 'boolean',
      description:
        'What is in the box is wrong. Turns the border and the message red and marks the input invalid for a ' +
        'screen reader. The page decides; the field does not.',
      default: false,
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description:
        'The line under the box — muted helper text on its own, the red reason when `invalid` is also set. ' +
        'This is where a bound belongs in words: "min" refuses a value, the message explains it.',
    },
    {
      name: 'presentation',
      attribute: 'presentation',
      type: 'text',
      // A CLOSED set, the same shape as the picklist's presentations and for the same reason.
      options: ['box', 'slider'],
      description:
        'How the same number is drawn: `box` (the default), or `slider`, a range control with the chosen ' +
        'value beside it. It is a presentation of this field rather than a different field — `min`, `max`, ' +
        '`step`, `required`, `disabled`, `invalid` and `message` all mean what they always did. THE SLIDER ' +
        'NEEDS A RANGE: with `min` or `max` missing it stays the box, because a range control with no bounds ' +
        'silently spans the platform\'s own 0–100, which has nothing to do with the field. The value is ' +
        'drawn beside the control because a range shows a position and never a number.',
      default: 'box',
    },
    {
      name: 'inline',
      attribute: 'inline',
      type: 'boolean',
      description:
        'Draw the record-detail variant instead of the boxed form control: no box, an accent underline, ' +
        'inheriting the surrounding type. The label and the message are hidden.',
      default: false,
    },
  ],

  // Plain DOM events, re-dispatched from the shadow root with `composed` so they cross the boundary.
  events: [
    { name: 'input', when: 'On every keystroke, after the value attribute has been written back.', carries: 'nothing' },
    { name: 'change', when: 'When the control is committed — focus leaves, Enter is pressed, or the stepper is used.', carries: 'nothing' },
  ],

  // Nothing to read back that cannot also be set. `value` can be read off the element, but a page can
  // set it too, so it is documented as an input rather than here.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the label, the box and the message.' },
    { token: '--caos-text-body', controls: 'The size of the label and of the typed number.' },
    { token: '--caos-space-1', controls: 'The gap between the label, the box and the message.' },
    { token: '--caos-space-3', controls: 'The vertical padding inside the box.' },
    { token: '--caos-radius', controls: 'The corner radius of the box.' },
    { token: '--caos-color-border', controls: 'The box outline at rest.' },
    { token: '--caos-color-border-strong', controls: 'The box outline while the field has focus.' },
    { token: '--caos-color-surface-2', controls: 'The fill of the box.' },
    { token: '--caos-color-text', controls: 'The colour of the typed number.' },
    { token: '--caos-color-text-muted', controls: 'The label, the placeholder, the message and disabled text.' },
    { token: '--caos-color-accent', controls: 'The hover outline, and the underline of the inline variant.' },
    { token: '--caos-accent-wash', controls: 'The tint that fills the box while it has focus, in place of a focus ring.' },
    { token: '--caos-color-danger', controls: 'The required asterisk, and the outline and message when invalid.' },
  ],

  parts: [
    { part: 'label', is: 'the label above the control' },
    { part: 'input', is: 'the inner number input — or the range, in the slider presentation; one part name, because it is one control' },
    { part: 'readout', is: 'the chosen value drawn beside the range, in the slider presentation only' },
    { part: 'message', is: 'the line under the control' },
  ],

  methods: [
    {
      name: 'focus',
      description:
        'Put the caret in the box. The shared implementation also tries to select what is there; a number ' +
        'input does not support selection, and the attempt is swallowed rather than thrown.',
      args: [
        {
          name: 'options',
          type: 'FocusOptions',
          description: 'The ordinary focus options — pass `{ preventScroll: true }` to focus without scrolling to it.',
        },
      ],
    },
  ],

  placing:
    'Placed as an item in a page section, and worth placing in the same column as other numbers: the ' +
    'tabular-figure marker is what makes a stack of them line up, and that only shows when they are stacked. ' +
    'The unit belongs in the label — the box holds the number and nothing else.',

  whenToUse:
    'A quantity, a measurement or an amount the person types, where the precision and the bounds are known ' +
    'up front. Reach for it over TextField whenever the value is genuinely a number, because that is what ' +
    'buys the alignment and the stepper.',

  insteadUse: [
    {
      name: 'FieldRenderer',
      when: 'The type is not known until the record is read — hand it the field metadata and it draws the right control, including the currency and percent read formats this piece does not do.',
    },
    { name: 'TextField', when: 'The value only looks numeric — a part number, a phone number, a tag with leading zeros.' },
    { name: 'DateField', when: 'The number is a date.' },
    { name: 'ProgressBar', when: 'The number is being reported rather than entered.' },
    { name: 'StatTile', when: 'It is a headline figure on a page rather than a field on a form.' },
  ],

  accessibility:
    'The control is a real number input with a generated id and a label pointing at it, so it is announced ' +
    'with its name, clicking the label focuses it, and the stepper and up/down keys work without anything ' +
    'being added. `required` and `invalid` are forwarded as `required` and `aria-invalid`. What is left to ' +
    'the author is the label — including the unit, since "48" announced on its own says nothing — and the ' +
    'message, which is the only place the bound is stated in words.',

  examples: [
    {
      key: 'measurement',
      label: 'A bounded measurement',
      summary: 'A measurement that cannot go below zero and moves in quarter-inches — the bounds carried by the control itself.',
      placement: {
        component: 'caos-number-field',
        inputs: { label: 'Diameter (in)', value: 48, min: 0, step: 0.25 },
      },
    },
    {
      key: 'refused',
      label: 'Out of range',
      summary:
        'The same field with the page refusing the value: red outline, and the bound restated underneath in words ' +
        'rather than left for the person to infer from a rejected keystroke.',
      placement: {
        component: 'caos-number-field',
        inputs: {
          label: 'Diameter (in)',
          value: -6,
          min: 0,
          step: 0.25,
          invalid: true,
          message: 'Diameter is a positive measurement.',
        },
      },
    },
  ],
};

export default docs;
